import { createRouter, createWebHistory } from "vue-router";
import { addModules } from "@/composables/cache/modules";
import {
  captureDepartingRouteLayout,
  scheduleDepartingRouteLayoutCleanup,
} from "@/composables/routeLayoutCleanup";

// Sandbox
import SandboxHome from "../views/sandbox/SandboxHome.vue";

// Utility
import UtilityHome from "../views/utility/UtilityHome.vue";

// Login
import LoginHome from "../views/login/LoginHome.vue";

// Logout
import LogoutHome from "../views/login/LogoutHome.vue";

// Loading
import LoadingHome from "../views/login/LoadingHome.vue";

// Setup
import SetupHome from "../views/setup/SetupHome.vue";

// Settings
import SettingsHome from "../views/settings/SettingsHome.vue";

// Home
import HomeView from "../views/HomeView.vue";

// Activity
import ActivityHome from "../views/activity/ActivityHome.vue";

// Help
import HelpHome from "../views/help/HelpHome.vue";

// Adders
import AdderHome from "../views/adder/AdderHome.vue";

// Bypass
import BypassHome from "../views/bypass/BypassHome.vue";

// Client
import ClientHome from "../views/client/ClientHome.vue";
import ClientView from "../views/client/ClientView.vue";

// Private
import PrivateHome from "../views/private/PrivateHome.vue";
import PrivateView from "../views/private/PrivateView.vue";
import PrivateAdd from "../views/private/PrivateAdd.vue";

// Public
import PublicHome from "../views/public/PublicHome.vue";
import PublicAdd from "../views/public/PublicAdd.vue";

// Retail
import RetailHome from "../views/retail/RetailHome.vue";
import RetailView from "../views/retail/RetailView.vue";

// TPA
import TPAHome from "../views/tpa/TPAHome.vue";
import TPAView from "../views/tpa/TPAView.vue";
import TPAAdd from "../views/tpa/TPAAdd.vue";

// Provider
import ProviderHome from "../views/provider/ProviderHome.vue";
import ProviderSync from "../views/provider/ProviderSync.vue";
import ProviderView from "../views/provider/ProviderView.vue";
import ProviderAdd from "../views/provider/ProviderAdd.vue";

// Provider
import HospitalHome from "../views/hospital/HospitalHome.vue";

// Tariff Mini
import TariffMiniHome from "../views/tariffMini/TariffMiniHome.vue";
import TariffMiniView from "../views/tariffMini/TariffMiniView.vue";

// Tariff Plus
import TariffPlusHome from "../views/tariffPlus/TariffPlusHome.vue";
import TariffPlusView from "../views/tariffPlus/TariffPlusView.vue";

// Claim
import ClaimHome from "../views/claim/ClaimHome.vue";
import ClaimView from "../views/claim/ClaimView.vue";
import ClaimAdd from "../views/claim/ClaimAdd.vue";

// Auth
import AuthHome from "../views/auth/AuthHome.vue";
import AuthView from "../views/auth/AuthView.vue";
import AuthAdd from "../views/auth/AuthAdd.vue";

// Advisory
import AdvisoryHome from "../views/advisory/AdvisoryHome.vue";

// Supervisors
import SupervisorHome from "../views/supervisor/SupervisorHome.vue";

// HOD
import HODHome from "../views/hod/HODHome.vue";

// Compliance
import ComplianceHome from "../views/compliance/ComplianceHome.vue";

// Finance
import FinanceHome from "../views/finance/FinanceHome.vue";

// Enrollee
import EnrolleeHome from "../views/enrollee/EnrolleeHome.vue";
import EnrolleeAdd from "../views/enrollee/EnrolleeAdd.vue";

// Payment
import PaymentHome from "../views/payment/PaymentHome.vue";

// Report
import ReportHome from "../views/report/ReportHome.vue";

// *** Care *** \\

// List
import ListHome from "../views/care/list/ListHome.vue";

// Encounter Authorization
import EncounterHome from "../views/care/encounter/EncounterHome.vue";

// Billing
import BillingHome from "../views/care/billing/BillingHome.vue";
import BillingView from "../views/care/billing/BillingView.vue";

// Settings (Care Users)
import UsersHome from "../views/care/user/UserHome.vue";

// *** Company *** \\

// Members
import MemberHome from "../views/company/member/MemberHome.vue";

// Invoices
import InvoiceHome from "../views/company/invoice/InvoiceHome.vue";

// Contract
import ContractHome from "../views/company/contract/ContractHome.vue";

// Provider
import HealthcareHome from "../views/company/healthcare/HealthcareHome.vue";

// Settings (Company Profiles)
import ProfilesHome from "../views/company/profile/ProfileHome.vue";

// *** User *** \\

// Provider
import MedicalHome from "../views/user/medical/MedicalHome.vue";
// History
import HistoryHome from "../views/user/history/HistoryHome.vue";
// Benefit
import BenefitsHome from "../views/user/benefits/BenefitsHome.vue";
// Profile
import ProfileHome from "../views/user/profile/ProfileHome.vue";

// authentication routing
import store from "../store/index";

// Routing guide 1
const authRequired = (to, from, next) => {
  if (!store.state.user) {
    next({ name: "login" });
    return;
  }

  if (to.name === "loading" || to.name === "home") {
    next();
    return;
  }

  const modules = Array.isArray(store.state.user.modules)
    ? store.state.user.modules
    : [];

  // load allowed modules for user
  const userPass = addModules()?.find(
    (x) =>
      modules.includes(x.to) && // ensure user has access to module
      x.type === store.state.user.type && // ensure user type matches module type
      to.name.includes(x.to) // ensure route name matches module route
  );

  if (userPass) {
    // Move on
    next();
  } else {
    // Redirect
    next({ name: "login" });
  }
};

const authOnly = (to, from, next) => {
  if (store.state.user) {
    next();
  } else {
    next({ name: "login" });
  }
};

const routes = [
  {
    path: "/sandbox",
    name: "sandbox",
    component: SandboxHome,
    beforeEnter: authRequired,
  },
  {
    path: "/utility/view/:clientId/:stampId",
    name: "utility",
    component: UtilityHome,
    props: true,
  },
  {
    path: "/login",
    name: "login",
    component: LoginHome,
  },
  {
    path: "/logout",
    name: "logout",
    component: LogoutHome,
  },
  {
    path: "/loading",
    name: "loading",
    component: LoadingHome,
  },
  {
    path: "/setup",
    name: "setup",
    component: SetupHome,
    beforeEnter: authRequired,
  },
  {
    path: "/settings",
    name: "settings",
    component: SettingsHome,
    beforeEnter: authRequired,
  },
  {
    path: "/",
    name: "home",
    component: HomeView,
    beforeEnter: authRequired,
  },
  {
    path: "/activity",
    name: "activity",
    component: ActivityHome,
    beforeEnter: authRequired,
  },
  {
    path: "/help",
    name: "help",
    component: HelpHome,
    beforeEnter: authOnly,
  },
  {
    path: "/adder",
    name: "adder",
    component: AdderHome,
    beforeEnter: authRequired,
  },
  {
    path: "/bypass",
    name: "bypass",
    component: BypassHome,
    beforeEnter: authRequired,
  },
  {
    path: "/client",
    name: "client",
    component: ClientHome,
    beforeEnter: authRequired,
  },
  {
    path: "/client/view/:id",
    name: "clientView",
    component: ClientView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/private",
    name: "private",
    component: PrivateHome,
    beforeEnter: authRequired,
  },
  {
    path: "/private/view/:id",
    name: "privateView",
    component: PrivateView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/private/add",
    name: "privateAdd",
    component: PrivateAdd,
    beforeEnter: authRequired,
  },
  {
    path: "/public",
    name: "public",
    component: PublicHome,
    beforeEnter: authRequired,
  },
  {
    path: "/public/add/:id",
    name: "publicAdd",
    component: PublicAdd,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/retail",
    name: "retail",
    component: RetailHome,
    beforeEnter: authRequired,
  },
  {
    path: "/retail/view/:id",
    name: "retailView",
    component: RetailView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/tpa",
    name: "tpa",
    component: TPAHome,
    beforeEnter: authRequired,
  },
  {
    path: "/tpa/view/:id",
    name: "tpaView",
    component: TPAView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/tpa/add",
    name: "tpaAdd",
    component: TPAAdd,
    beforeEnter: authRequired,
  },
  {
    path: "/hospital",
    name: "hospital",
    component: HospitalHome,
    beforeEnter: authRequired,
  },
  {
    path: "/provider",
    name: "provider",
    component: ProviderHome,
    beforeEnter: authRequired,
  },
  {
    path: "/provider/sync",
    name: "providerSync",
    component: ProviderSync,
    beforeEnter: authRequired,
  },
  {
    path: "/provider/view/:id",
    name: "providerView",
    component: ProviderView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/provider/add",
    name: "providerAdd",
    component: ProviderAdd,
    beforeEnter: authRequired,
  },
  {
    path: "/tariff-mini",
    name: "tariffMini",
    component: TariffMiniHome,
    beforeEnter: authRequired,
  },
  {
    path: "/tariff-mini/view/:id",
    name: "tariffMiniView",
    component: TariffMiniView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/tariff-plus",
    name: "tariffPlus",
    component: TariffPlusHome,
    beforeEnter: authRequired,
  },
  {
    path: "/tariff-plus/view/:id",
    name: "tariffPlusView",
    component: TariffPlusView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/claim",
    name: "claim",
    component: ClaimHome,
    beforeEnter: authRequired,
  },
  {
    path: "/claim/view/:clusterId",
    name: "claimView",
    component: ClaimView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/claim/add/",
    name: "claimAdd",
    component: ClaimAdd,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/auth",
    name: "auth",
    component: AuthHome,
    beforeEnter: authRequired,
  },
  {
    path: "/auth/view/:encounterId",
    name: "authView",
    component: AuthView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/auth/add",
    name: "authAdd",
    component: AuthAdd,
    beforeEnter: authRequired,
  },
  {
    path: "/enrollee",
    name: "enrollee",
    component: EnrolleeHome,
    beforeEnter: authRequired,
  },
  {
    path: "/enrollee/add",
    name: "enrolleeAdd",
    component: EnrolleeAdd,
    beforeEnter: authRequired,
  },
  {
    path: "/advisory",
    name: "advisory",
    component: AdvisoryHome,
    beforeEnter: authRequired,
  },
  {
    path: "/supervisor",
    name: "supervisor",
    component: SupervisorHome,
    beforeEnter: authRequired,
  },
  {
    path: "/hod",
    name: "hod",
    component: HODHome,
    beforeEnter: authRequired,
  },
  {
    path: "/compliance",
    name: "compliance",
    component: ComplianceHome,
    beforeEnter: authRequired,
  },
  {
    path: "/finance",
    name: "finance",
    component: FinanceHome,
    beforeEnter: authRequired,
  },
  {
    path: "/payment",
    name: "payment",
    component: PaymentHome,
    beforeEnter: authRequired,
  },
  {
    path: "/report",
    name: "report",
    component: ReportHome,
    beforeEnter: authRequired,
  },
  {
    path: "/care/billing",
    name: "billing",
    component: BillingHome,
    beforeEnter: authRequired,
  },
  {
    path: "/care/billing/view/:clusterId",
    name: "billingView",
    component: BillingView,
    props: true,
    beforeEnter: authRequired,
  },
  {
    path: "/care/list",
    name: "list",
    component: ListHome,
    beforeEnter: authRequired,
  },
  {
    path: "/care/encounter",
    name: "encounter",
    component: EncounterHome,
    beforeEnter: authRequired,
  },
  {
    path: "/care/encounter/add/:encounterId?", 
    name: "encounterAdd",
    redirect: { name: "encounter", hash: "#start" },
  },
  {
    path: "/care/users",
    name: "users",
    component: UsersHome,
    beforeEnter: authRequired,
  },
  {
    path: "/company/members",
    name: "members",
    component: MemberHome,
    beforeEnter: authRequired,
  },
  {
    path: "/company/invoices",
    name: "invoices",
    component: InvoiceHome,
    beforeEnter: authRequired,
  },
  {
    path: "/company/contract",
    name: "contract",
    component: ContractHome,
    beforeEnter: authRequired,
  },
  {
    path: "/company/healthcare",
    name: "healthcare",
    component: HealthcareHome,
    beforeEnter: authRequired,
  },
  {
    path: "/company/profiles",
    name: "profiles",
    component: ProfilesHome,
    beforeEnter: authRequired,
  },
  {
    path: "/user/medical",
    name: "medical",
    component: MedicalHome,
    beforeEnter: authRequired,
  },
  {
    path: "/user/history",
    name: "history",
    component: HistoryHome,
    beforeEnter: authRequired,
  },
  {
    path: "/user/benefits",
    name: "benefits",
    component: BenefitsHome,
    beforeEnter: authRequired,
  },
  {
    path: "/user/profile",
    name: "profile",
    component: ProfileHome,
    beforeEnter: authRequired,
  },
  {
    path: "/about",
    name: "about",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/AboutView.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }
    return { top: 0 }; // Always scroll to the top for non-hash routes
  },
});

router.afterEach((to, from, failure) => {
  if (failure) {
    return;
  }

  const departingLayout = captureDepartingRouteLayout(to, from);
  scheduleDepartingRouteLayoutCleanup(departingLayout);
});

export default router;
